import React, { useState } from "react";
import { utils, writeFile } from "xlsx";

function SalesReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salesData, setSalesData] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // New state for search input

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const fetchSalesReport = () => {
    setError(null);
    setSearchQuery(""); // Reset search when fetching new data
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }

    const payload = { start_date: startDate, end_date: endDate };

    fetch("https://stock.krishtec.co.in/stock/get_sales_report.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Sales Report Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setSalesData(Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch sales report error:", err);
        setSalesData([]);
        setError("Failed to load sales report: " + err.message);
      });
  };

  const downloadExcel = () => {
    if (salesData.length === 0) {
      setError("No data to download.");
      return;
    }

    const filteredData = salesData.filter((item) =>
      item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (filteredData.length === 0) {
      setError("No matching data to download.");
      return;
    }

    const worksheetData = filteredData.map((item) => ({
      "Sale Date": formatDate(item.sale_date),
      "Product ID": item.product_id,
      "Product Name": item.product_name,
      "Stock Sold": item.stock_sold,
      "Store ID": item.store_id,
      "Store Name": item.store_name,
      "Price Per Unit": item.price_per_unit,
      "Total Sale Amount": item.total_sale_amount,
    }));

    const worksheet = utils.json_to_sheet(worksheetData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Sales Report");
    worksheet["!cols"] = [
      { wch: 12 }, // Sale Date
      { wch: 12 }, // Product ID
      { wch: 20 }, // Product Name
      { wch: 12 }, // Stock Sold
      { wch: 12 }, // Store ID
      { wch: 15 }, // Store Name
      { wch: 15 }, // Price Per Unit
      { wch: 15 }, // Total Sale Amount
    ];
    writeFile(workbook, `Sales_Report_${startDate}_to_${endDate}.xlsx`);
  };

  // Filter sales data based on search query
  const filteredSalesData = salesData.filter((item) =>
    item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center p-6 bg-gray-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">Sales Report</h1>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Date Inputs and Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-end bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex gap-4">
            <button
              onClick={fetchSalesReport}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Fetch Report
            </button>
            <button
              onClick={downloadExcel}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {salesData.length > 0 && (
          <div className="mb-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">Search Products</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter product name..."
              className="w-full sm:w-1/3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Sales Table */}
        {salesData.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Sale Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Product ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Stock Sold
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Store ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Store Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Price Per Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Total Sale Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSalesData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatDate(item.sale_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.product_id || "N/A"}</td>
                      <td className="px-6 py-4 text-gray-700">{item.product_name || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.stock_sold || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.store_id || "N/A"}</td>
                      <td className="px-6 py-4 text-gray-700">{item.store_name || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        Rs.{item.price_per_unit ? Number(item.price_per_unit).toFixed(2) : "0.00"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        Rs.{item.total_sale_amount ? Number(item.total_sale_amount).toFixed(2) : "0.00"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredSalesData.length === 0 && searchQuery && (
              <div className="text-center text-gray-500 py-6">
                No products found matching "{searchQuery}".
              </div>
            )}
          </div>
        )}

        {salesData.length === 0 && !error && startDate && endDate && (
          <div className="text-center text-gray-500 mt-6">No sales data found for the selected date range.</div>
        )}
      </div>
    </div>
  );
}

export default SalesReport;